import { useState, useMemo, useEffect, Fragment } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Badge } from '@/components/ui/badge';
import { Plus, Pencil, Trash2, MapPin, Package, ChevronDown, ChevronRight, Search, MoreVertical } from 'lucide-react';
import { mockLocations } from '@/lib/mock-settings-data';
import { mockAssets } from '@/lib/mock-data';
import type { Location } from '@/types/settings';
import { useMode } from '@/providers/ModeProvider';
import { useGetBackendUrl } from '@/hooks/use-get-backend-url';
import { useToast } from '@/hooks/use-toast';
import { ProductTour } from '@/components/ui/product-tour';
import { useTour } from '@/contexts/TourContext';
import { useLocationsTour } from '@/hooks/use-locations-tour';
import { EvoFeatureFeedbackCollector } from '@/components/FeedbackCollector';
import { useLocationsPermissions } from '@/hooks/use-permissions';

// Helper function to count assets for a sub-location
function countSubLocationAssets(
  locationName: string,
  subLocationName: string,
  assets: typeof mockAssets
): number {
  return assets.filter(
    (asset) => asset.location === locationName && asset.subLocation === subLocationName
  ).length;
}

export default function LocationsPage(): React.ReactElement {
  const navigate = useNavigate();
  const { isDemo } = useMode();
  const backendUrl = useGetBackendUrl();
  const { toast } = useToast();
  const tour = useLocationsTour();
  const { setOnStartTour } = useTour();
  const { canManage: canManageLocations } = useLocationsPermissions();
  
  const [locations, setLocations] = useState<Location[]>(mockLocations);
  const [searchQuery, setSearchQuery] = useState('');
  
  // Expanded locations state
  const [expandedLocations, setExpandedLocations] = useState<Set<string>>(new Set());

  // Register tour
  useEffect(() => {
    setOnStartTour(() => tour.startTour);
    return () => setOnStartTour(null);
  }, [setOnStartTour, tour]);

  // Fetch locations from API in live mode
  useEffect(() => {
    if (!isDemo) {
      fetchLocations();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDemo]);

  // Refresh locations when the page becomes visible (e.g., when navigating back)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden && !isDemo) {
        fetchLocations();
      }
    };

    const handleFocus = () => {
      if (!isDemo) {
        fetchLocations();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleFocus);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDemo]);

  const fetchLocations = async () => {
    try {
      const response = await fetch(`${backendUrl}/api/Settings/locations`, {
        credentials: 'include',
      });
      if (response.ok) {
        const data = await response.json();
        const uniqueLocations = Array.from(
          new Map(data.map((loc: Location) => [loc.id, loc])).values()
        ) as Location[];
        setLocations(uniqueLocations);
      }
    } catch (error) {
      console.error('Error fetching locations:', error);
    }
  };

  // Calculate asset counts dynamically from mockAssets (only in demo mode)
  const locationsWithAssetCounts = useMemo(() => {
    if (isDemo) {
      return locations.map((location) => {
        const assetCount = mockAssets.filter(
          (asset) => asset.location === location.name
        ).length;
        
        // Calculate sub-location asset counts using helper function to reduce nesting
        const subLocationsWithCounts = (location.subLocations ?? []).map((subLoc) => ({
          ...subLoc,
          assetCount: countSubLocationAssets(location.name, subLoc.name, mockAssets),
        }));
        
        return {
          ...location,
          assetCount,
          subLocations: subLocationsWithCounts,
        };
      });
    }
    
    // In live mode, use the asset counts from the backend
    return locations;
  }, [locations, isDemo]);


  const handleOpenDialog = (location?: Location) => {
    if (location) {
      // Navigate to detail page for editing
      navigate(`/administration/locations/${location.id}`);
    } else {
      // Navigate to create page
      navigate('/administration/locations/new?mode=create');
    }
  };


  const handleDelete = async (id: string) => {
    if (!confirm('Are you sure you want to delete this location? This will also delete all sub-locations.')) {
      return;
    }
    
    if (!isDemo) {
      try {
                const response = await fetch(`${backendUrl}/api/Settings/locations/${id}`, {
                  method: 'DELETE',
                  credentials: 'include',
                });
        if (response.ok) {
          toast({
            title: 'Success',
            description: 'Location deleted successfully',
          });
          await fetchLocations();
        } else {
          // Try to get the error message from the response
          let errorMessage = 'Failed to delete location. Please try again.';
          try {
            const errorData = await response.json();
            if (errorData.message) {
              errorMessage = errorData.message;
            }
          } catch {
            // If parsing fails, use default message
          }
          
          toast({
            title: 'Error',
            description: errorMessage,
            variant: 'destructive',
          });
        }
      } catch (error) {
        console.error('Error deleting location:', error);
        toast({
          title: 'Error',
          description: 'Failed to delete location. Please try again.',
          variant: 'destructive',
        });
      }
    } else {
      setLocations(locations.filter((loc) => loc.id !== id));
      toast({
        title: 'Success',
        description: 'Location deleted successfully',
      });
    }
  };

  const handleAssetCountClick = (locationName: string, subLocationName?: string) => {
    if (subLocationName) {
      navigate(`/assets?location=${encodeURIComponent(locationName)}&subLocation=${encodeURIComponent(subLocationName)}`);
    } else {
      navigate(`/assets?location=${encodeURIComponent(locationName)}`);
    }
  };

  const toggleLocationExpanded = (locationId: string) => {
    setExpandedLocations((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(locationId)) {
        newSet.delete(locationId);
      } else {
        newSet.add(locationId);
      }
      return newSet;
    });
  };

  const filteredLocations = locationsWithAssetCounts.filter((location) =>
    location.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    location.address?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    location.locationManager?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="flex h-full w-full flex-col overflow-hidden">
      {/* Page Header - Following Transaction History Pattern */}
      <div className="border-b bg-card p-6 flex-shrink-0">
        <div className="flex items-center justify-between gap-4" data-tour="locations-header">
          <div className="flex items-center gap-3">
            <MapPin className="h-6 w-6 text-primary" />
            <div>
              <h1 className="text-2xl font-bold tracking-tight">Locations</h1>
              <p className="text-sm text-muted-foreground mt-1">
                Manage physical locations and sub-locations where assets are stored
              </p>
            </div>
          </div>
          
          {/* Search Bar and Add Button - Right aligned */}
                    <div className="flex items-center gap-3">
                      <EvoFeatureFeedbackCollector feature="Locations" />
                      <div className="relative w-80" data-tour="locations-search">
                        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                        <Input
                          placeholder="Search locations..."
                          value={searchQuery}
                          onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setSearchQuery(e.target.value)}
                          className="pl-9"
                        />
                      </div>
                      {canManageLocations && (
                        <Button onClick={() => handleOpenDialog()} data-tour="locations-new-button">
                          <Plus className="mr-2 h-4 w-4" />
                          Add Location
                        </Button>
                      )}
                    </div>
                  </div>
                </div>

            {/* Table Container - Fills remaining space */}
      <div className="flex-1 overflow-hidden flex flex-col min-h-0">
        <div className="flex-1 overflow-auto">
          <Table data-tour="locations-table">
            <TableHeader className="sticky top-0 z-10 bg-slate-50 dark:bg-slate-900">
              <TableRow className="border-b-2 border-slate-200 dark:border-slate-700 hover:bg-slate-100 dark:hover:bg-slate-800">
                <TableHead className="h-12 font-semibold w-12"></TableHead>
                <TableHead className="h-12 font-semibold">Location Name</TableHead>
                <TableHead className="h-12 font-semibold">Address</TableHead>
                <TableHead className="h-12 font-semibold">Manager</TableHead>
                <TableHead className="h-12 font-semibold text-center">Assets</TableHead>
                <TableHead className="h-12 font-semibold text-right"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
                {filteredLocations.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center text-muted-foreground h-96">
                      No locations found
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredLocations.map((location, index) => {
                    const isExpanded = expandedLocations.has(location.id);
                    const hasSubLocations = location.subLocations && location.subLocations.length > 0;
                    
                    return (
                      <Fragment key={location.id}>
                        {/* Parent Location Row */}
                        <TableRow
                          className={`
                            ${index % 2 === 0 ? 'bg-white dark:bg-slate-950' : 'bg-slate-50/50 dark:bg-slate-900/50'}
                            hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors border-b border-slate-100 dark:border-slate-800
                          `}
                        >
                          <TableCell>
                            {hasSubLocations && (
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-6 w-6"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  toggleLocationExpanded(location.id);
                                }}
                              >
                                {isExpanded ? (
                                  <ChevronDown className="h-4 w-4" />
                                ) : (
                                  <ChevronRight className="h-4 w-4" />
                                )}
                              </Button>
                            )}
                          </TableCell>
                          <TableCell className="font-medium">
                            <div className="flex items-center gap-2">
                              <button
                                onClick={() => navigate(`/administration/locations/${location.id}`)}
                                className="text-primary hover:underline cursor-pointer font-medium"
                              >
                                {location.name}
                              </button>
                              {hasSubLocations && (
                                <Badge variant="outline" className="ml-2">
                                  {location.subLocations?.length ?? 0}
                                </Badge>
                              )}
                            </div>
                          </TableCell>
                          <TableCell>
                            <span className="text-sm">{location.address ?? '-'}</span>
                          </TableCell>
                          <TableCell>{location.locationManager ?? '-'}</TableCell>
                          <TableCell className="text-center">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleAssetCountClick(location.name);
                              }}
                              className="inline-flex items-center gap-1 text-primary hover:underline cursor-pointer"
                            >
                              <Package className="h-3 w-3" />
                              <Badge variant="secondary">{location.assetCount}</Badge>
                            </button>
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex justify-end gap-2">
                              {canManageLocations && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    navigate(`/administration/locations/${location.id}`);
                                  }}
                                  title="Edit"
                                >
                                  <Pencil className="h-4 w-4" />
                                </Button>
                              )}
                              {canManageLocations && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleDelete(location.id);
                                  }}
                                  className="text-destructive hover:text-destructive"
                                  title="Delete"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>

                        {/* Sub-Location Rows (when expanded) */}
                        {isExpanded && hasSubLocations && location.subLocations?.map((subLoc) => (
                          <TableRow
                            key={`${location.id}-${subLoc.id}`}
                            className={`
                              ${index % 2 === 0 ? 'bg-slate-50/50 dark:bg-slate-900/50' : 'bg-white dark:bg-slate-950'}
                              hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors border-b border-slate-100 dark:border-slate-800 border-l-4 border-l-primary/30
                            `}
                          >
                            <TableCell></TableCell>
                            <TableCell className="font-medium">
                              <div className="flex items-center gap-2 pl-8">
                                <span className="text-muted-foreground">└─</span>
                                <button
                                  onClick={() => navigate(`/administration/locations/${location.id}?tab=sublocations`)}
                                  className="text-primary hover:underline cursor-pointer font-medium"
                                >
                                  {subLoc.name}
                                </button>
                              </div>
                            </TableCell>
                            <TableCell>
                              <span className="text-sm text-muted-foreground">-</span>
                            </TableCell>
                            <TableCell>{subLoc.locationManager ?? '-'}</TableCell>
                            <TableCell className="text-center">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleAssetCountClick(location.name, subLoc.name);
                                }}
                                className="inline-flex items-center gap-1 text-primary hover:underline cursor-pointer"
                              >
                                <Package className="h-3 w-3" />
                                <Badge variant="secondary">{subLoc.assetCount ?? 0}</Badge>
                              </button>
                            </TableCell>
                            <TableCell className="text-right">
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    onClick={(e) => e.stopPropagation()}
                                  >
                                    <MoreVertical className="h-4 w-4" />
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end">
                                  <DropdownMenuItem
                                    onClick={(e: React.MouseEvent) => {
                                      e.stopPropagation();
                                      navigate(`/administration/locations/${location.id}?tab=sublocations`);
                                    }}
                                  >
                                    <Pencil className="h-4 w-4 mr-2" />
                                    View Location
                                  </DropdownMenuItem>
                                </DropdownMenuContent>
                              </DropdownMenu>
                            </TableCell>
                          </TableRow>
                        ))}
                      </Fragment>
                    );
                  })
                )}
            </TableBody>
          </Table>
        </div>
      </div>

      <ProductTour
        steps={tour.tourSteps}
        isOpen={tour.isTourOpen}
        onClose={tour.closeTour}
        onComplete={tour.completeTour}
      />
    </div>
  );
}
